import React, { useState } from 'react';
import { contactsAPI } from '../../../services/api';
import { Button, Card, Modal } from '../../../components/ui';
import toast from 'react-hot-toast';

const DESIGNATIONS = [
  'Principal', 'Vice Principal', 'Dean', 'Vice Dean',
  'Head of Department (HOD)', 'Professor', 'Associate Professor',
  'Assistant Professor', 'Director', 'Registrar',
  'Placement Officer', 'Admin Officer', 'Trustee / Management',
  'IT Coordinator', 'Other'
];

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

const EMPTY_FORM = { name: '', designation: '', department: '', phone: '', email: '', is_primary: false, notes: '' };

export default function ContactsTab({ lead, onLeadUpdate }) {
  const contacts = lead.contacts || [];
  const [modal, setModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null); // null = add mode, object = edit mode
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const openAdd = () => {
    setEditingContact(null);
    setForm(EMPTY_FORM);
    setModal(true);
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact.name || '',
      designation: contact.designation || '',
      department: contact.department || '',
      phone: contact.phone || '',
      email: contact.email || '',
      is_primary: contact.is_primary || false,
      notes: contact.notes || ''
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      let res;
      if (editingContact) {
        res = await contactsAPI.update(lead._id, editingContact._id, form);
      } else {
        res = await contactsAPI.add(lead._id, form);
      }
      // Backend returns updated contacts array; patch it onto the lead
      onLeadUpdate(prev => ({ ...prev, contacts: res.data?.data || [] }));
      toast.success(editingContact ? 'Contact updated' : 'Contact added');
      setModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save contact');
    } finally { setSaving(false); }
  };

  const remove = async (contact) => {
    if (!window.confirm(`Remove ${contact.name}?`)) return;
    setDeletingId(contact._id);
    try {
      const res = await contactsAPI.remove(lead._id, contact._id);
      onLeadUpdate(prev => ({ ...prev, contacts: res.data?.data || [] }));
      toast.success('Contact removed');
    } catch {
      toast.error('Failed to remove contact');
    } finally { setDeletingId(null); }
  };

  const setPrimary = async (contact) => {
    if (contact.is_primary) return;
    try {
      const res = await contactsAPI.update(lead._id, contact._id, { is_primary: true });
      onLeadUpdate(prev => ({ ...prev, contacts: res.data?.data || [] }));
      toast.success(`${contact.name} set as primary contact`);
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>
          Contacts <span style={{ fontSize: 13, fontWeight: 400, color: '#94a3b8' }}>({contacts.length})</span>
        </h3>
        <Button size="sm" variant="primary" onClick={openAdd}>+ Add Contact</Button>
      </div>

      {contacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
          <p style={{ fontSize: 32, marginBottom: 8 }}>👤</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>No contacts added yet</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>Add the Dean, HOD, Professor or any key person from this institution</p>
          <Button size="sm" variant="primary" onClick={openAdd} style={{ marginTop: 16 }}>+ Add First Contact</Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {contacts.map(c => (
            <Card key={c._id} style={{ padding: '14px 16px', border: c.is_primary ? '2px solid #C9A84C' : '1px solid #e2e8f0', position: 'relative' }}>
              {c.is_primary && (
                <span style={{ position: 'absolute', top: 10, right: 10, fontSize: 10, background: '#C9A84C', color: '#fff', padding: '2px 7px', borderRadius: 8, fontWeight: 700 }}>PRIMARY</span>
              )}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: '#1B2A6B', flexShrink: 0 }}>
                  {c.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 2 }}>{c.name}</p>
                  {(c.designation || c.department) && (
                    <p style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>
                      {c.designation}{c.designation && c.department ? ' · ' : ''}{c.department}
                    </p>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone}`} style={{ display: 'block', fontSize: 12, color: '#2563eb', textDecoration: 'none', marginBottom: 1 }}>📞 {c.phone}</a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} style={{ display: 'block', fontSize: 12, color: '#2563eb', textDecoration: 'none', marginBottom: 1 }}>✉️ {c.email}</a>
                  )}
                  {c.notes && (
                    <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>{c.notes}</p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                {!c.is_primary && (
                  <Button size="sm" variant="ghost" onClick={() => setPrimary(c)} style={{ fontSize: 11 }}>Set Primary</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} style={{ fontSize: 11 }}>Edit</Button>
                <Button size="sm" variant="danger" onClick={() => remove(c)} disabled={deletingId === c._id} style={{ fontSize: 11, marginLeft: 'auto' }}>
                  {deletingId === c._id ? '…' : 'Remove'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editingContact ? `Edit — ${editingContact.name}` : 'Add Contact'}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} placeholder="Full name" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Designation</label>
              <select value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} style={sel}>
                <option value="">Select designation…</option>
                {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Department</label>
            <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} style={inp} placeholder="e.g. Computer Science, Management, Admin…" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={inp} placeholder="+91 98765 43210" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Email</label>
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inp} placeholder="name@institution.edu.in" />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, minHeight: 60, resize: 'vertical' }} placeholder="e.g. Best time to call: morning. Key decision maker." />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.is_primary} onChange={e => setForm(f => ({ ...f, is_primary: e.target.checked }))} />
            <span style={{ color: '#475569' }}>Set as primary contact for this lead</span>
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : (editingContact ? 'Update Contact' : 'Add Contact')}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
